import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Loader2, Menu, X, Phone, Mail, ArrowRight, ChevronDown, CheckCircle2, Star, MapPin, Clock,
  Home, Layers, Wrench, Square, PaintRoller, Hammer, ShieldCheck, PencilLine, Send, ThumbsUp,
} from "lucide-react";

const FONTS = "https://fonts.googleapis.com/css2?family=Outfit:wght@600;700;800;900&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap";
const clean = (s) => (s || "").replace(/[#*_`>[\]]/g, "").trim();

const ICONS = [
  [/(roof|shingle|gutter)/i, Home], [/(siding|exterior|wrap)/i, Layers], [/(window)/i, Square],
  [/(paint|coating|finish)/i, PaintRoller], [/(deck|carpen|build|remodel|construct)/i, Hammer],
  [/(plumb|electric|hvac|repair|install|maintenance)/i, Wrench],
];
const iconFor = (t) => (ICONS.find(([re]) => re.test(t)) || [null, Wrench])[1];

// Pull service names from a block's markdown headings / bold lines.
function extractServices(md) {
  const out = [];
  (md || "").split("\n").forEach((l) => {
    const h = l.match(/^#{1,4}\s+(.+)/) || l.match(/^\*\*(.+?)\*\*\s*$/);
    if (h) {
      let name = clean(h[1]).replace(/\s+in\s+.+$/i, "").replace(/\bservices?\b/i, "").trim();
      if (name && name.length < 42 && !/^(our|why|about|contact|faq|home|overview)/i.test(name)) out.push(name);
    }
  });
  return [...new Set(out)].slice(0, 6);
}
function firstPara(md) {
  const p = (md || "").split("\n").map(clean).filter((l) => l.length > 40);
  return p[0] || clean((md || "").split("\n").find((l) => clean(l)) || "");
}
function parseHero(md) {
  const lines = (md || "").split("\n").map(clean).filter((l) => l && !/^(call now|get started|contact us|schedule|learn more|view)/i.test(l) && l.length > 3);
  return { headline: lines[0] || "", sub: (lines[1] || "").length > 25 ? lines[1] : (lines.slice(1, 3).find((l) => l.length > 25) || "") };
}

const MD = {
  p: (p) => <p className="mb-4 text-slate-600 leading-relaxed" {...p} />,
  ul: (p) => <ul className="space-y-2 my-4" {...p} />,
  li: (p) => <li className="flex gap-2.5 text-slate-600"><CheckCircle2 size={18} className="mt-0.5 flex-shrink-0 text-[var(--brand)]" /><span>{p.children}</span></li>,
  h1: (p) => <h2 className="text-2xl font-bold text-slate-900 mt-6 mb-3 font-['Outfit']" {...p} />,
  h2: (p) => <h3 className="text-xl font-bold text-slate-900 mt-5 mb-2 font-['Outfit']" {...p} />,
  h3: (p) => <h4 className="text-lg font-semibold text-slate-900 mt-4 mb-2 font-['Outfit']" {...p} />,
  strong: (p) => <strong className="font-semibold text-slate-900" {...p} />,
};
const Body = ({ children }) => <div className="[&>*:first-child]:mt-0 text-[15px] font-['Inter']"><ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{children}</ReactMarkdown></div>;

export default function PublicSitePreview() {
  const { token } = useParams();
  const [data, setData] = useState(undefined);
  const [page, setPage] = useState("home");
  const [menu, setMenu] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);

  useEffect(() => {
    import("@/lib/api").then(({ default: api }) => {
      api.get(`/ai/studio/preview/${token}`).then((r) => setData(r.data)).catch(() => setData(null));
      api.post(`/ai/studio/preview/${token}/view`, {}).catch(() => {});
    });
  }, [token]);

  const go = (p) => { setPage(p); setMenu(false); setSvcOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const model = useMemo(() => {
    if (!data) return null;
    const blocks = data.blocks || [];
    const byId = (id) => blocks.find((b) => b.block === id);
    const svcBlock = byId("services") || byId("service_pages");
    const allText = blocks.map((b) => b.result).join("\n");
    const phone = (allText.match(/(\(?\d{3}\)?[\s.\-]?\d{3}[\s.\-]?\d{4})/) || [])[1] || "";
    const email = (allText.match(/[\w.+-]+@[\w-]+\.[a-z]{2,}/i) || [])[0] || "";
    let services = svcBlock ? extractServices(svcBlock.result) : [];
    if (services.length < 2) services = ["Our Services"];
    return {
      blocks, byId, phone, email, services,
      hero: byId("hero") || blocks[0],
      about: byId("about"),
      svcBlock,
      faq: byId("faq"),
      images: data.images || {},
      name: data.business_name || "Your Business",
      color: data.brand_color || "#C9A227",
    };
  }, [data]);

  if (data === undefined) return <div className="min-h-screen bg-slate-100 flex items-center justify-center text-slate-500 font-['Inter']"><Loader2 className="animate-spin mr-2" size={18} /> Loading…</div>;
  if (data === null) return <div className="min-h-screen bg-slate-100 flex items-center justify-center text-slate-500 font-['Inter']" data-testid="preview-not-found">This preview link isn't valid or has expired.</div>;

  const { color, name, images, phone, email, services, hero, about, svcBlock, faq } = model;
  const heroP = hero ? parseHero(hero.result) : { headline: name, sub: "" };
  const NAV = [["home", "Home"], ["services", "Services"], ["projects", "Projects"], ["about", "About"], ["contact", "Contact"]];
  const gallery = [images.hero, ...(images.services || []), images.about].filter(Boolean);

  const Logo = () => data.logo
    ? <img src={data.logo} alt={name} className="h-11 w-auto object-contain" data-testid="preview-logo" />
    : (
      <div className="flex items-center gap-2.5" data-testid="preview-logo">
        <div className="h-10 w-10 rounded-lg flex items-center justify-center font-extrabold text-white font-['Outfit'] text-sm" style={{ backgroundColor: color }}>{name.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}</div>
        <span className="font-extrabold text-slate-900 leading-tight font-['Outfit'] text-[15px]">{name}</span>
      </div>
    );

  const CTA = ({ className = "", label = "Get a Free Estimate" }) => (
    <button onClick={() => go("contact")} data-testid="preview-cta-estimate" className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold text-sm text-white transition-transform hover:scale-[1.03] ${className}`} style={{ backgroundColor: color }}>{label}</button>
  );

  const trust = [
    ["Licensed & Insured", ShieldCheck], ["Experienced Team", Star], ["Free Estimates", CheckCircle2], ["Locally Owned", MapPin],
  ];

  return (
    <div className="min-h-screen bg-white text-slate-800 font-['Inter'] antialiased" style={{ "--brand": color }} data-testid="public-site-preview">
      <Helmet><title>{`${name} — Website Preview`}</title><link rel="stylesheet" href={FONTS} /></Helmet>

      <div className="text-center text-[10px] uppercase tracking-[0.3em] py-1.5 font-['JetBrains_Mono'] bg-[#0b1220]" style={{ color }} data-testid="preview-ribbon">Website Preview · by ATF WebWorks</div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200" data-testid="preview-header">
        <div className="max-w-6xl mx-auto px-5 h-[68px] flex items-center justify-between gap-4">
          <button onClick={() => go("home")}><Logo /></button>
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map(([id, label]) => id === "services" ? (
              <div key={id} className="relative" onMouseEnter={() => setSvcOpen(true)} onMouseLeave={() => setSvcOpen(false)}>
                <button onClick={() => go("services")} data-testid="nav-services" className={`px-3 py-2 text-sm font-medium flex items-center gap-1 ${page === id ? "text-[var(--brand)]" : "text-slate-700 hover:text-[var(--brand)]"}`}>Services <ChevronDown size={14} /></button>
                {svcOpen && (
                  <div className="absolute top-full left-0 bg-white border border-slate-200 shadow-xl rounded-md py-2 min-w-[190px]">
                    {services.map((s, i) => <button key={i} onClick={() => go("services")} className="block w-full text-left px-4 py-1.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-[var(--brand)]">{s}</button>)}
                  </div>
                )}
              </div>
            ) : (
              <button key={id} onClick={() => go(id)} data-testid={`nav-${id}`} className={`px-3 py-2 text-sm font-medium ${page === id ? "text-[var(--brand)]" : "text-slate-700 hover:text-[var(--brand)]"}`}>{label}</button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <div className="hidden lg:block"><CTA /></div>
            <button className="lg:hidden text-slate-700" onClick={() => setMenu(!menu)} data-testid="preview-mobile-menu">{menu ? <X size={24} /> : <Menu size={24} />}</button>
          </div>
        </div>
        {menu && <div className="lg:hidden border-t border-slate-200 bg-white px-5 py-3 space-y-1">{NAV.map(([id, label]) => <button key={id} onClick={() => go(id)} className="block w-full text-left py-2 text-slate-700 font-medium">{label}</button>)}<div className="pt-2"><CTA /></div></div>}
      </header>

      {/* HOME */}
      {page === "home" && (
        <>
          <section className="relative min-h-[70vh] flex items-center px-6" data-testid="preview-hero">
            {images.hero && <img src={images.hero} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid="preview-hero-image" />}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0b1220]/92 via-[#0b1220]/70 to-transparent" />
            <div className="relative max-w-6xl mx-auto w-full py-20"><div className="max-w-2xl text-white">
              <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold leading-[1.06] font-['Outfit'] uppercase tracking-tight">{heroP.headline}</h1>
              {heroP.sub && <p className="mt-4 text-lg text-white/75 max-w-lg">{heroP.sub}</p>}
              <div className="flex flex-wrap gap-3 mt-8">
                <CTA />
                <button onClick={() => go("services")} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold text-sm border border-white/40 text-white hover:bg-white/10">View our services <ArrowRight size={15} /></button>
              </div>
            </div></div>
          </section>

          {/* Service cards */}
          <section className="max-w-6xl mx-auto px-6 -mt-12 relative z-10 pb-16" data-testid="preview-services-cards">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {services.slice(0, 4).map((s, i) => { const Ic = iconFor(s); return (
                <button key={i} onClick={() => go("services")} className="bg-white rounded-xl border border-slate-200 shadow-lg p-5 text-left hover:-translate-y-1 transition-transform" data-testid={`preview-service-card-${i}`}>
                  <div className="h-10 w-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${color}1a`, color }}><Ic size={20} /></div>
                  <div className="font-bold text-slate-900 font-['Outfit']">{s}</div>
                  <div className="text-slate-500 text-xs mt-1">Learn more →</div>
                </button>
              ); })}
            </div>
          </section>

          {/* Trust bar */}
          <section className="border-y border-slate-200 bg-slate-50" data-testid="preview-trust">
            <div className="max-w-6xl mx-auto px-6 py-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
              {trust.map(([t, Ic], i) => <div key={i} className="flex items-center gap-2.5 text-slate-700"><Ic size={20} style={{ color }} /><span className="text-sm font-semibold font-['Outfit']">{t}</span></div>)}
            </div>
          </section>

          {about && (
            <section className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-12 gap-12 items-center" data-testid="preview-about-teaser">
              <div className="lg:col-span-6"><div className="text-xs font-semibold uppercase tracking-[0.2em] mb-3" style={{ color }}>About us</div><Body>{about.result.split("\n").slice(0, 6).join("\n")}</Body><button onClick={() => go("about")} className="mt-3 text-sm font-semibold" style={{ color }}>More about us →</button></div>
              {images.about && <img src={images.about} alt="" className="lg:col-span-6 w-full h-72 object-cover rounded-2xl" />}
            </section>
          )}

          <CtaBand color={color} name={name} phone={phone} onClick={() => go("contact")} />
        </>
      )}

      {page === "services" && (
        <Page title="Our Services" color={color} img={images.services?.[0] || images.hero}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {services.map((s, i) => { const Ic = iconFor(s); return (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid={`preview-service-${i}`}>
                <div className="h-11 w-11 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${color}1a`, color }}><Ic size={22} /></div>
                <div className="font-bold text-slate-900 font-['Outfit'] text-lg">{s}</div>
              </div>
            ); })}
          </div>
          {svcBlock && <Body>{svcBlock.result}</Body>}
        </Page>
      )}

      {page === "projects" && (
        <Page title="Recent Projects" color={color} img={images.hero}>
          {gallery.length ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="preview-gallery">
              {gallery.map((src, i) => (
                <div key={i} className="relative overflow-hidden rounded-xl group">
                  <img src={src} alt="" className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500" data-testid={`preview-project-${i}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4"><span className="text-white text-sm font-semibold font-['Outfit']">{services[i % services.length]}</span></div>
                </div>
              ))}
            </div>
          ) : <p className="text-slate-500">Project gallery coming soon.</p>}
        </Page>
      )}

      {page === "about" && (
        <Page title={`About ${name}`} color={color} img={images.about || images.hero}>
          {about ? <Body>{about.result}</Body> : <p className="text-slate-500">About content coming soon.</p>}
          {faq && <div className="mt-12 pt-8 border-t border-slate-200"><div className="text-xs font-semibold uppercase tracking-[0.2em] mb-4" style={{ color }}>FAQ</div><Body>{faq.result}</Body></div>}
        </Page>
      )}

      {page === "contact" && (
        <Page title="Get in Touch" color={color} img={images.hero}>
          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-4 font-['Outfit']">Request a free estimate</h3>
              <form onSubmit={(e) => e.preventDefault()} className="space-y-3" data-testid="preview-contact-form">
                <input className="w-full border border-slate-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)]" placeholder="Your name" />
                <div className="grid grid-cols-2 gap-3">
                  <input className="w-full border border-slate-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)]" placeholder="Phone" />
                  <input className="w-full border border-slate-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)]" placeholder="Email" />
                </div>
                <textarea rows={4} className="w-full border border-slate-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)]" placeholder="How can we help?" />
                <button className="inline-flex items-center gap-2 px-6 py-3 rounded-md font-semibold text-sm text-white" style={{ backgroundColor: color }}>Send request <Send size={15} /></button>
              </form>
            </div>
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-3">
                {phone && <div className="flex items-center gap-3 text-slate-700"><Phone size={18} style={{ color }} /> <a href={`tel:${phone}`} className="font-semibold">{phone}</a></div>}
                {email && <div className="flex items-center gap-3 text-slate-700"><Mail size={18} style={{ color }} /> <a href={`mailto:${email}`} className="font-semibold break-all">{email}</a></div>}
                <div className="flex items-center gap-3 text-slate-700"><Clock size={18} style={{ color }} /> <span className="text-sm">Mon–Fri, business hours <span className="text-slate-400">[confirm]</span></span></div>
              </div>
              <iframe title="map" className="w-full h-64 rounded-xl border border-slate-200" loading="lazy"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=-88.5,30.2,-85.0,35.0&layer=mapnik`} />
            </div>
          </div>
        </Page>
      )}

      {/* Footer */}
      <footer className="bg-[#0b1220] text-white/80" data-testid="preview-footer">
        <div className="max-w-6xl mx-auto px-6 py-12 grid sm:grid-cols-3 gap-8">
          <div><div className="font-extrabold text-white text-lg font-['Outfit'] mb-2">{name}</div><p className="text-sm text-white/50">Quality workmanship you can count on.</p></div>
          <div><div className="font-semibold text-white mb-3 font-['Outfit'] text-sm uppercase tracking-wide">Contact</div>
            {phone && <div className="flex items-center gap-2 text-sm mb-1.5"><Phone size={14} style={{ color }} /> {phone}</div>}
            {email && <div className="flex items-center gap-2 text-sm break-all"><Mail size={14} style={{ color }} /> {email}</div>}
          </div>
          <div><div className="font-semibold text-white mb-3 font-['Outfit'] text-sm uppercase tracking-wide">Explore</div>{NAV.map(([id, l]) => <button key={id} onClick={() => go(id)} className="block text-sm text-white/60 hover:text-white py-0.5">{l}</button>)}</div>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-[11px] text-white/40 font-['JetBrains_Mono'] px-6">Content &amp; layout preview — final design produced during your build · Powered by ATF WebWorks</div>
      </footer>

      <FeedbackWidget token={token} color={color} alreadyLeft={data.feedback_left} />
    </div>
  );
}

const Page = ({ title, color, img, children }) => (
  <>
    <section className="relative h-52 flex items-center px-6">
      {img && <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      <div className="absolute inset-0 bg-[#0b1220]/80" />
      <div className="relative max-w-6xl mx-auto w-full"><h1 className="text-3xl sm:text-4xl font-extrabold text-white font-['Outfit'] uppercase tracking-tight">{title}</h1></div>
    </section>
    <section className="max-w-6xl mx-auto px-6 py-14">{children}</section>
  </>
);

const CtaBand = ({ color, name, phone, onClick }) => (
  <section className="py-16 px-6 text-center" style={{ backgroundColor: color }} data-testid="preview-cta-band">
    <div className="max-w-2xl mx-auto text-[#0b1220]">
      <h2 className="text-3xl font-extrabold font-['Outfit']">Ready to get started?</h2>
      <p className="mt-2 opacity-80">Contact {name} today for your free estimate.</p>
      <div className="flex flex-wrap justify-center gap-3 mt-6">
        <button onClick={onClick} className="px-6 py-3 rounded-md font-semibold text-sm bg-[#0b1220] text-white">Get a Free Estimate</button>
        {phone && <a href={`tel:${phone}`} className="px-6 py-3 rounded-md font-semibold text-sm bg-white text-[#0b1220] inline-flex items-center gap-2"><Phone size={15} /> {phone}</a>}
      </div>
    </div>
  </section>
);

function FeedbackWidget({ token, color, alreadyLeft }) {
  const [open, setOpen] = useState(false);
  const [sentiment, setSentiment] = useState(null);
  const [msg, setMsg] = useState("");
  const [sent, setSent] = useState(alreadyLeft);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!sentiment) return; setBusy(true);
    try { const { default: api } = await import("@/lib/api"); await api.post(`/ai/studio/preview/${token}/feedback`, { sentiment, message: msg }); setSent(true); setOpen(false); }
    catch { /* ignore */ } finally { setBusy(false); }
  };
  if (sent) return <div className="fixed bottom-5 right-5 z-50 bg-[#0b1220] text-white rounded-xl px-4 py-3 text-sm flex items-center gap-2 shadow-2xl font-['JetBrains_Mono']" data-testid="preview-feedback-thanks"><CheckCircle2 size={16} style={{ color }} /> Thanks!</div>;
  return (
    <div className="fixed bottom-5 right-5 z-50 font-['Inter']" data-testid="preview-feedback">
      {open ? (
        <div className="w-[300px] bg-[#0b1220] text-white rounded-2xl p-4 shadow-2xl border border-white/10">
          <div className="flex justify-between items-center mb-3"><span className="font-semibold text-sm">What do you think?</span><button onClick={() => setOpen(false)} data-testid="preview-feedback-close"><X size={16} className="text-white/50" /></button></div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button onClick={() => setSentiment("love")} data-testid="preview-feedback-love" className="flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium" style={sentiment === "love" ? { background: color, color: "#0b1220" } : { border: "1px solid rgba(255,255,255,.2)" }}><ThumbsUp size={14} /> Love it</button>
            <button onClick={() => setSentiment("changes")} data-testid="preview-feedback-changes" className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium ${sentiment === "changes" ? "bg-white text-[#0b1220]" : "border border-white/20"}`}><PencilLine size={14} /> Tweak it</button>
          </div>
          <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} placeholder="Add a note…" data-testid="preview-feedback-message" className="w-full bg-white/5 border border-white/15 rounded-lg p-2.5 text-sm mb-2 focus:outline-none" />
          <button onClick={submit} disabled={!sentiment || busy} data-testid="preview-feedback-submit" className="w-full py-2.5 rounded-lg font-semibold text-sm text-[#0b1220] disabled:opacity-50 flex items-center justify-center gap-2" style={{ background: color }}>{busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send</button>
        </div>
      ) : (
        <button onClick={() => setOpen(true)} data-testid="preview-feedback-open" className="flex items-center gap-2 px-5 py-3 rounded-full shadow-2xl font-semibold text-sm text-[#0b1220]" style={{ background: color }}><PencilLine size={16} /> Share your thoughts</button>
      )}
    </div>
  );
}
